import { useEffect, useRef, useState } from 'react';

import { cn } from '@v2/shared/lib/utils';
import NamedIcon from '@v2/shared/components/NamedIcon';
import { SaleCategory } from '@v2/shared/lib/megaSale';
import { saleChromeHeight, SCROLL_CLEARANCE_PX } from '@v2/shared/lib/megaSale/scrollToSaleTarget';

type Props = {
  categories: SaleCategory[];
  selectedCategoryId: number | null;
  onSelect: (categoryId: number) => void;
};

/**
 * The sale's sections, as a bar of pills across the top.
 *
 * This replaces the rail that used to run down the left. A rail costs a fixed column of
 * the page at every width and gives nothing back that a horizontal bar does not, and on
 * this page that column is worth a column of products — which is the whole reason the
 * legacy page put its sections in a `secondary-nav` of pills and not in a sidebar.
 *
 * Full-bleed, as the legacy bar was: no `Container`, and its `md:px-6 2xl:px-10` is the
 * header's own padding rather than `Container`'s `px-5`, so the two bars line up at every
 * width instead of the navbar stopping short of a header that does not.
 *
 * Desktop only, as the legacy bar was (`hidden-xs`, and `responsive:move` relocated the
 * list into the drawer below that). On a phone the same categories are what the hamburger
 * opens — see SaleCategoriesDialog — because a wrapping bar of pills would eat the screen
 * a phone needs for products.
 *
 * Sticky under the fixed header, which is what `affix` did for the legacy bar. It
 * publishes its own height so the category headings can stick underneath it rather than
 * behind it; see `--sale-nav-height` in the page.
 *
 * Buttons rather than `#sect_wrapper` anchors: an anchor scrolls the element to the top
 * of the viewport with the fixed header over it, so the page lands showing the second row
 * of a category and none of its heading.
 */
export default function CategoryNav({ categories, selectedCategoryId, onSelect }: Props) {
  const ref = useRef<HTMLElement>(null);
  const active = useActiveCategory(categories, selectedCategoryId);

  /*
   * The bar's height, as a custom property, measured rather than stated.
   *
   * The pills wrap onto a second row on a narrow desktop and on a sale with many
   * sections, so this is not one number — and the headings that stick below it have to
   * know the number it currently is. Zero when the bar is hidden, which is what a phone
   * gets: a `display: none` element measures 0 and the headings then clear the header
   * alone, correctly.
   */
  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const measure = () =>
      document.documentElement.style.setProperty('--sale-nav-height', `${element.offsetHeight}px`);

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--sale-nav-height');
    };
  }, []);

  if (categories.length === 0) {
    return null;
  }

  return (
    <nav
      ref={ref}
      aria-label="Sale categories"
      className="hidden md:block sticky top-[var(--sale-header-height,82px)] z-[30] bg-white border-b-[1.5px] border-slate-200 shadow-sm"
    >
      <ul className="list-none flex flex-wrap justify-center items-center gap-x-2 gap-y-1 m-0 py-2 px-0 md:px-6 2xl:px-10">
        {categories.map((category) => {
          const current = active === category.id;

          return (
            <li key={category.id}>
              <button
                type="button"
                onClick={() => onSelect(category.id)}
                aria-current={current ? 'true' : undefined}
                className={cn(
                  'flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors',
                  current
                    ? 'bg-primary text-white'
                    : 'text-slate-700 hover:bg-primary hover:text-white active:bg-primary-700'
                )}
              >
                {category.icon && <NamedIcon name={category.icon} prefix="fas" fixedWidth />}
                {category.name}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * Which pill to light up: the section the reader is currently in.
 *
 * The legacy bar got this from Bootstrap's scrollspy, and a pill bar without it is
 * strictly worse than the rail it replaces — the reader loses the one cue that says where
 * they are in a sale of a dozen sections.
 *
 * Deliberately its own state rather than writing to the sale's `selectedCategoryId`.
 * That value also drives the page's scroll-to effect, so a scroll listener writing to it
 * would mean scrolling sets the selection, which scrolls the page, which sets the
 * selection. Clicking a pill still calls `select()` — that is a real navigation — and
 * this reads as the fallback until the first scroll measurement lands.
 */
function useActiveCategory(categories: SaleCategory[], selectedCategoryId: number | null): number | null {
  const [scrolled, setScrolled] = useState<number | null>(null);

  useEffect(() => {
    if (categories.length === 0) {
      return;
    }

    /*
     * The last heading that has reached its sticky resting place is the section being
     * read. `getComputedStyle(heading).top` is that resting place — the offset the
     * heading itself is stuck at — so this asks the layout rather than restating the
     * header and navbar heights here and drifting from them.
     *
     * "Reached" allows the clearance the scroll leaves, and not a pixel: clicking a pill
     * parks its heading deliberately just short of the resting place so the bar cannot
     * clip it, and without that allowance the one section you had just asked for was the
     * one section the bar would not light up.
     */
    const pick = () => {
      let current: number | null = null;

      for (const category of categories) {
        const heading = document.getElementById(`sect${category.id}`);

        /*
         * A section with no sticky heading — a section of postcards opens on an explainer
         * that scrolls away — is reached when its own top reaches the chrome a heading
         * would have rested under. Skipped instead, the section before it stayed lit all
         * the way through the postcards, and clicking their pill lit the wrong one.
         */
        const marker = heading ?? document.getElementById(`sect_wrapper${category.id}`);

        if (!marker) {
          continue;
        }

        const stickyTop = heading ? parseFloat(getComputedStyle(heading).top) || 0 : saleChromeHeight();

        if (marker.getBoundingClientRect().top <= stickyTop + SCROLL_CLEARANCE_PX + 1) {
          current = category.id;
        }
      }

      setScrolled(current);
    };

    pick();

    window.addEventListener('scroll', pick, { passive: true });
    window.addEventListener('resize', pick);

    return () => {
      window.removeEventListener('scroll', pick);
      window.removeEventListener('resize', pick);
    };
  }, [categories]);

  return scrolled ?? selectedCategoryId;
}
